import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { CountryCode } from '@dayetopia/types';
import { PrismicImage } from '@prismicio/react';
import { WindowLocation, useLocation } from '@reach/router';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import Video from '@components/VideoModule';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import useDesktop from '../shared/useDesktop';

interface Slide {
  product_image?: {
    gatsbyImageData: IGatsbyImageData;
    alt: string;
    url: string;
  };
  video?: {
    url: string;
    alt: string;
  };
  thumbnail?: {
    gatsbyImageData: IGatsbyImageData;
    alt: string;
  };
}

interface Props {
  slides: Slide[];
}

const renderNavigation = (
  slides: Slide[],
  slider: Slider | null,
  setNavSlider: (slider: Slider | null) => void,
  country: CountryCode,
  location: WindowLocation
) => (
  <ThumbsWrap>
    <Slider
      asNavFor={slider}
      ref={(ref: Slider) => setNavSlider(ref)}
      slidesToShow={slides.length}
      focusOnSelect={true}
      vertical={true}
      centerMode={true}
    >
      {slides.map((slide, index) => (
        <ThumbItem key={`${index}-${slide.video?.url}`}>
          {slide?.thumbnail ? (
            <GatsbyImage image={slide.thumbnail?.gatsbyImageData} alt={replaceText(slide.thumbnail.alt, country, location) ?? ''} />
          ) : null}
          {slide?.product_image ? (
            <GatsbyImage image={slide.product_image?.gatsbyImageData} alt={replaceText(slide?.product_image?.alt, country, location) ?? ''} />
          ) : null}
        </ThumbItem>
      ))}
    </Slider>
  </ThumbsWrap>
);

const renderSlider = (
  slides: Slide[],
  imageSize: number,
  navSlider: Slider | null,
  sliderSettings: any,
  setSlider: (slider: Slider | null) => void
) => (
  <MainSliderWrap>
    <Slider asNavFor={navSlider} ref={(ref: Slider) => setSlider(ref)} {...sliderSettings}>
      {slides.map((slide, index) => {
        if (slide.video) {
          return (
            <ItemWrap key={`${index}-${slide.video.url}`}>
              <Video mp4={slide.video.url} cover={false} width={864} height={720} />
            </ItemWrap>
          );
        }
        if (slide.product_image) {
          return (
            <ItemWrap key={`${index}-${slide.product_image.url}`}>
              <PrismicImage
                field={slide.product_image}
                imgixParams={{ width: imageSize, height: imageSize }}
                width={imageSize}
                height={imageSize}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </ItemWrap>
          );
        }
        return null;
      })}
    </Slider>
  </MainSliderWrap>
);

const ProductImageSlider: React.FC<Props> = ({ slides }) => {
  const location = useLocation();
  const { country } = useCountry();
  const isDesktop = useDesktop(1024);
  const [slider, setSlider] = useState<Slider | null>(null);
  const [navSlider, setNavSlider] = useState<Slider | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [show, setShow] = useState(false);
  const imageSize = isDesktop ? 600 : 400;
  const firstImageSlide = slides.find((slide) => slide.product_image);

  useEffect(() => {
    setLoaded(true);
    setTimeout(() => {
      setShow(true);
    }, 1000);
  }, []);

  const settings = {
    dots: !isDesktop,
    dotsClass: 'slick-dots',
    lazyLoad: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false
  };

  return (
    <Container>
      <ImageWrap $show={!show}>
        <PrismicImage field={firstImageSlide?.product_image} width="690" height={600} loading="eager" fetchpriority="high" />
      </ImageWrap>
      {loaded ? (
        <SliderWrap id="slider-wrap" $show={show}>
          {isDesktop && renderNavigation(slides, slider, setNavSlider, country, location)}
          {renderSlider(slides, imageSize, navSlider, settings, setSlider)}
        </SliderWrap>
      ) : null}
    </Container>
  );
};

export default ProductImageSlider;

const Container = styled.div`
  position: relative;
  width: 690px;
  overflow: hidden;
  max-width: 100%;
  height: 600px;

  @media (max-width: 1300px) {
    height: 500px;
  }

  @media (max-width: 1024px) {
    aspect-ratio: 1/1;
    height: auto;
  }
`;

const SliderWrap = styled.div<{ $show: boolean }>`
  display: flex;
  opacity: ${(props) => (props.$show ? '1' : '0')};
  transition: opacity 0.5s ease-in-out;
  pointer-events: ${(props) => (props.$show ? 'all' : 'none')};
  align-items: flex-start;
  gap: 30px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  @media (max-width: 1300px) {
    width: 590px;
    height: 500px;
  }

  @media (max-width: 1024px) {
    width: 100%;
    height: auto;

    &#slider-wrap {
      height: 100%;
    }

    & .slick-slide {
      margin-bottom: -7px;
    }
  }
`;

const ImageWrap = styled(SliderWrap)`
  display: flex;
  justify-content: flex-start;
  aspect-ratio: 1 / 1;
  padding-left: 90px;

  & > img {
    object-fit: cover;
    border: 2px solid ${Colors.cream};
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    height: auto;

    @media (max-width: 1024px) {
      border-width: 0;
    }
  }

  @media (max-width: 1024px) {
    padding-left: 0;
  }
`;

const MainSliderWrap = styled.div`
  width: 600px;
  height: 600px;
  border-radius: 2px;
  background-color: ${Colors.cream};
  border: 2px solid ${Colors.cream};

  & .slick-dots {
    bottom: 14px;
  }

  @media (max-width: 1300px) {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 1024px) {
    max-width: 100%;
    height: auto;
    border-width: 0;
  }
`;

const ThumbsWrap = styled.div`
  width: 60px;

  .slick-track {
    height: auto !important;
    display: flex;
    flex-direction: column;
    gap: 15px;
    transform: none !important;
  }

  & .slick-current > div > div {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }
`;

const ItemWrap = styled.div`
  height: 600px;
  align-items: center;

  & > .gatsby-image-wrapper {
    height: 100%;
  }

  & img {
    width: 100%;
    object-fit: cover;
    min-height: 100%;

    @media (max-width: 1200px) {
      height: 100%;
      width: 100%;
    }

    @media (max-width: 1024px) {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
    }
  }

  & .video-container {
    height: 100%;
    position: relative;

    @media (max-width: 1024px) {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
    }

    & .video-wrapper {
      padding-bottom: 100%;
    }

    video {
      border-radius: 2px;
      object-fit: cover;
    }
  }

  @media (max-width: 1300px) {
    height: 500px;
  }

  @media (max-width: 1024px) {
    height: auto;
    position: relative;

    &:before {
      content: '';
      display: block;
      padding-top: 100%;
    }
  }
`;

const ThumbItem = styled.div`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 2px;
  border: 1px solid ${Colors.cream};
  background: linear-gradient(180deg, #dbe8f1 0%, #faedea 100%);
  cursor: pointer;

  & > .gatsby-image-wrapper {
    height: 100%;
  }
`;
